/**
 * AUTO_Insurance_Wizard_v06 (React + TypeScript + React-Bootstrap)
 * Version: v06
 * Date: 2025-09-06 (America/Denver)
 *
 * v06 updates & guarantees:
 * - Right-side info panel (no popups) listing only fields that are missing/invalid
 * - Finish is gray/disabled until valid; turns green when ready
 * - Required fields marked; green ✓ shown on each valid field
 * - Help text effect plays once per slide, then remains static
 * - Greys out during submit; summary after success
 */

import React, {
  useMemo,
  useState,
  useCallback,
  PropsWithChildren,
  useEffect,
  useRef,
} from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  Carousel,
  Col,
  Container,
  Form,
  InputGroup,
  ProgressBar,
  Row,
  Spinner,
} from "react-bootstrap";

/* ============================================================
   Declarations (tunable variables & types)
   ============================================================ */
const VERSION = "v06";
const SHOW_VERSION = true; // toggle version badge on/off
const TEST_MODE = true; // true = console.log payload; false = POST to API
const API_GATEWAY_URL =
  "https://your-api-id.execute-api.us-east-1.amazonaws.com/prod/submit"; // <-- replace

const SLIDE_ANIMATION_MS = 450; // carousel slide duration
const SHOW_PROGRESS_BAR = true;

// --- Help text effect controls ---
export type HelpEffect = "none" | "typewriter" | "fade";
/** Choose the global help text effect (runs once per slide) */
const HELP_EFFECT: HelpEffect = "typewriter"; // "none" | "typewriter" | "fade"
const TYPEWRITER_SPEED_MS = 20;
const TYPEWRITER_CURSOR = true;
const FADE_DURATION_MS = 400;

const LABELS = {
  title: "Auto Insurance Wizard",
  subtitle: "Tell us about you, your car, and your coverages",
  stepTitles: ["Contact", "Vehicle", "Drivers", "Coverage", "Summary"],
  buttons: {
    back: "Back",
    next: "Next",
    finish: "Finish",
    edit: "Edit",
    submit: "Submit",
  },
  notices: {
    required: "Required fields are marked with *",
  },
} as const;

const STEP_HELP = [
  {
    title: "Why we ask for your address",
    text:
      "Auto rates depend on your garaging ZIP and where the vehicle is kept. Accurate contact and address help us match you with the right carriers.",
  },
  {
    title: "Vehicle details matter",
    text:
      "VIN, year, make, and model determine safety features and repair costs—major factors in pricing.",
  },
  {
    title: "Driver history & license",
    text:
      "Age, licensing history, and recent accidents/tickets influence eligibility and discounts.",
  },
  {
    title: "Coverage preferences",
    text:
      "Choose limits and deductibles that fit your risk tolerance and lender requirements.",
  },
] as const;

/* ============================================================
   Types & defaults
   ============================================================ */
export type WizardData = {
  // Contact & Address
  fullName: string;
  email: string;
  phone: string;
  street: string;
  city: string;
  state: string; // 2-letter
  zip: string; // 5 or 9

  // Vehicle
  vehicleYear: string;
  vehicleMake: string;
  vehicleModel: string;
  vin: string; // 17 chars
  ownership: string; // Owned | Financed | Leased
  vehicleUse: string; // Commute | Business | Pleasure | Rideshare
  annualMileage: string;
  garagingZip: string;

  // Driver
  dob: string; // MM/DD/YYYY or YYYY-MM-DD
  dlNumber: string;
  dlState: string; // 2-letter
  yearsLicensed: string;
  accidents5y: string; // integer
  tickets5y: string; // integer
  sr22Required: string; // optional Yes/No

  // Coverage
  liabilityLimit: string; // required
  collisionDeductible: string; // required
  comprehensiveDeductible: string; // required
  uninsuredMotorist: string; // optional
  medicalPayments: string; // optional
  roadsideAssist: string; // optional Yes/No
  rentalReimbursement: string; // optional
};

const defaultData: WizardData = {
  // Contact & Address
  fullName: "",
  email: "",
  phone: "",
  street: "",
  city: "",
  state: "",
  zip: "",

  // Vehicle
  vehicleYear: "",
  vehicleMake: "",
  vehicleModel: "",
  vin: "",
  ownership: "",
  vehicleUse: "",
  annualMileage: "",
  garagingZip: "",

  // Driver
  dob: "",
  dlNumber: "",
  dlState: "",
  yearsLicensed: "",
  accidents5y: "",
  tickets5y: "",
  sr22Required: "",

  // Coverage
  liabilityLimit: "",
  collisionDeductible: "",
  comprehensiveDeductible: "",
  uninsuredMotorist: "",
  medicalPayments: "",
  roadsideAssist: "",
  rentalReimbursement: "",
};

/* ============================================================
   Effects (typewriter/fade)
   ============================================================ */
const TypewriterText: React.FC<{
  text: string;
  speedMs?: number;
  showCursor?: boolean;
  triggerKey?: string | number;
  className?: string;
}> = ({
  text,
  speedMs = TYPEWRITER_SPEED_MS,
  showCursor = TYPEWRITER_CURSOR,
  triggerKey,
  className,
}) => {
  const [idx, setIdx] = useState<number>(0);
  const tRef = useRef<number | null>(null);

  useEffect(() => {
    setIdx(0);
  }, [triggerKey, text]);

  useEffect(() => {
    if (idx >= text.length) return;
    tRef.current = window.setTimeout(() => setIdx((i) => i + 1), speedMs);
    return () => {
      if (tRef.current) window.clearTimeout(tRef.current);
    };
  }, [idx, text.length, speedMs]);

  const done = idx >= text.length;
  return (
    <span className={className}>
      {text.slice(0, idx)}
      {showCursor && !done ? <span className="tw-cursor">|</span> : null}
    </span>
  );
};

const FadeText: React.FC<{
  text: string;
  durationMs?: number;
  triggerKey?: string | number;
  className?: string;
}> = ({ text, durationMs = FADE_DURATION_MS, triggerKey, className }) => {
  const [visible, setVisible] = useState<boolean>(false);
  useEffect(() => {
    setVisible(false);
    const t = window.setTimeout(() => setVisible(true), 10);
    return () => window.clearTimeout(t);
  }, [triggerKey, text]);

  return (
    <span
      className={className}
      style={{
        opacity: visible ? 1 : 0,
        transition: `opacity ${durationMs}ms ease`,
        display: "inline-block",
      }}
    >
      {text}
    </span>
  );
};

/* ============================================================
   UI helpers
   ============================================================ */
export type HelpPanelProps = {
  title: string;
  text: string;
  className?: string;
  effect?: HelpEffect;
  triggerKey?: string | number;
};
const HelpPanel: React.FC<HelpPanelProps> = ({
  title,
  text,
  className,
  effect = "none",
  triggerKey,
}) => (
  <Alert variant="info" className={`mb-3 ${className ?? ""}`.trim()}>
    <div className="fw-semibold mb-1">{title}</div>
    <div className="small text-muted">
      {effect === "typewriter" ? (
        <TypewriterText text={text} triggerKey={triggerKey} />
      ) : effect === "fade" ? (
        <FadeText text={text} triggerKey={triggerKey} />
      ) : (
        text
      )}
    </div>
  </Alert>
);

export type FieldNoteProps = PropsWithChildren<{ className?: string }>;
const FieldNote: React.FC<FieldNoteProps> = ({ children, className }) => (
  <div className={`form-text ${className ?? ""}`.trim()}>{children}</div>
);

/** Utility validations */
const isNumeric = (s: string) => /^\d+(\.\d+)?$/.test(s.trim());
const isInt = (s: string) => /^\d+$/.test(s.trim());
const isEmail = (s: string) => /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(s.trim());
const digitsOnly = (s: string) => s.replace(/\D/g, "");
const isZip = (s: string) => /^\d{5}(?:-\d{4})?$/.test(s.trim());
const isState2 = (s: string) => /^[A-Za-z]{2}$/.test(s.trim());
const isYear = (s: string) => {
  const y = parseInt(s, 10);
  const now = new Date().getFullYear();
  return Number.isFinite(y) && y >= 1981 && y <= now + 1;
};
const isVIN = (s: string) => /^[A-HJ-NPR-Z0-9]{17}$/i.test(s.trim());
const isDOB = (s: string) => {
  const v = s.trim();
  // Accept MM/DD/YYYY or YYYY-MM-DD
  const mmdd = /^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{2,4})$/;
  const ymd = /^(\d{4})[\/\-](\d{1,2})[\/\-](\d{1,2})$/;
  let d: Date | null = null;
  if (mmdd.test(v)) {
    const [, m, d2, y] = v.match(mmdd)!;
    d = new Date(parseInt(y.length === 2 ? `20${y}` : y, 10), parseInt(m, 10) - 1, parseInt(d2, 10));
  } else if (ymd.test(v)) {
    const [, y, m, d2] = v.match(ymd)!;
    d = new Date(parseInt(y, 10), parseInt(m, 10) - 1, parseInt(d2, 10));
  } else {
    const n = new Date(v);
    if (!isNaN(n.valueOf())) d = n;
  }
  if (!d) return false;
  const age = (Date.now() - d.getTime()) / (1000 * 60 * 60 * 24 * 365.25);
  return age >= 15; // basic sanity check
};

/* ============================================================
   Validation map (field-wise)
   ============================================================ */
export type ValidMap = Record<keyof WizardData, boolean>;
export type ErrorItem = { field: keyof WizardData; label: string; reason: string };

const FIELD_LABEL: Record<keyof WizardData, string> = {
  // Contact
  fullName: "Full Name",
  email: "Email",
  phone: "Phone",
  street: "Street",
  city: "City",
  state: "State",
  zip: "ZIP",

  // Vehicle
  vehicleYear: "Vehicle Year",
  vehicleMake: "Vehicle Make",
  vehicleModel: "Vehicle Model",
  vin: "VIN",
  ownership: "Ownership",
  vehicleUse: "Vehicle Use",
  annualMileage: "Annual Mileage",
  garagingZip: "Garaging ZIP",

  // Driver
  dob: "Date of Birth",
  dlNumber: "Driver's License #",
  dlState: "DL State",
  yearsLicensed: "Years Licensed",
  accidents5y: "Accidents (5 yrs)",
  tickets5y: "Tickets (5 yrs)",
  sr22Required: "SR-22 Required",

  // Coverage
  liabilityLimit: "Liability Limit",
  collisionDeductible: "Collision Deductible",
  comprehensiveDeductible: "Comprehensive Deductible",
  uninsuredMotorist: "Uninsured Motorist",
  medicalPayments: "Medical Payments",
  roadsideAssist: "Roadside Assistance",
  rentalReimbursement: "Rental Reimbursement",
};

function buildValidity(data: WizardData): { valid: ValidMap; errors: ErrorItem[] } {
  const valid: ValidMap = {
    // Contact
    fullName: data.fullName.trim().length > 1,
    email: isEmail(data.email),
    phone: digitsOnly(data.phone).length >= 10,
    street: data.street.trim().length > 3,
    city: data.city.trim().length > 1,
    state: isState2(data.state),
    zip: isZip(data.zip),

    // Vehicle
    vehicleYear: isYear(data.vehicleYear),
    vehicleMake: data.vehicleMake.trim().length > 1,
    vehicleModel: data.vehicleModel.trim().length > 1,
    vin: isVIN(data.vin),
    ownership: !!data.ownership,
    vehicleUse: !!data.vehicleUse,
    annualMileage: isNumeric(data.annualMileage) && parseFloat(data.annualMileage) > 0,
    garagingZip: isZip(data.garagingZip || data.zip),

    // Driver
    dob: isDOB(data.dob),
    dlNumber: data.dlNumber.trim().length >= 4,
    dlState: isState2(data.dlState),
    yearsLicensed: isNumeric(data.yearsLicensed),
    accidents5y: isInt(data.accidents5y || "0"),
    tickets5y: isInt(data.tickets5y || "0"),
    sr22Required: true, // optional

    // Coverage
    liabilityLimit: !!data.liabilityLimit,
    collisionDeductible: !!data.collisionDeductible,
    comprehensiveDeductible: !!data.comprehensiveDeductible,
    uninsuredMotorist: true, // optional
    medicalPayments: true, // optional
    roadsideAssist: true, // optional
    rentalReimbursement: true, // optional
  };

  const errors: ErrorItem[] = [];
  (Object.keys(valid) as (keyof WizardData)[]).forEach((k) => {
    if (!valid[k]) {
      let reason = "missing or invalid";
      if (k === "email") reason = "invalid email";
      if (k === "phone") reason = "phone too short";
      if (k === "state" || k === "dlState") reason = "use 2-letter code (e.g., CO)";
      if (k === "zip" || k === "garagingZip") reason = "5 digits (or ZIP+4)";
      if (k === "vehicleYear") reason = "enter a 4-digit year";
      if (k === "vin") reason = "VIN must be 17 characters";
      if (k === "annualMileage" || k === "yearsLicensed") reason = "must be a number";
      if (k === "accidents5y" || k === "tickets5y") reason = "enter 0 if none";
      if (k === "liabilityLimit" || k === "collisionDeductible" || k === "comprehensiveDeductible")
        reason = "required";
      errors.push({ field: k, label: FIELD_LABEL[k], reason });
    }
  });

  return { valid, errors };
}

/* ============================================================
   Validated Control wrapper (adds ✓ when valid)
   ============================================================ */
const ValidWrap: React.FC<{ valid: boolean; children: React.ReactNode }> = ({
  valid,
  children,
}) => (
  <InputGroup>
    {children}
    {valid && <InputGroup.Text aria-label="valid">✓</InputGroup.Text>}
  </InputGroup>
);

/* ============================================================
   Step subcomponents (help effect runs once per slide)
   ============================================================ */
export type StepProps = {
  data: WizardData;
  setData: (partial: Partial<WizardData>) => void;
  helpEffect: HelpEffect;
  helpKey: string | number;
  valid: ValidMap;
};

const Step1Contact: React.FC<StepProps> = ({ data, setData, helpEffect, helpKey, valid }) => (
  <Card className="shadow-sm">
    <Card.Body>
      <HelpPanel
        title={STEP_HELP[0].title}
        text={STEP_HELP[0].text}
        effect={helpEffect}
        triggerKey={helpKey}
      />

      <Form.Group className="mb-3">
        <Form.Label>Full Name *</Form.Label>
        <ValidWrap valid={valid.fullName}>
          <Form.Control
            value={data.fullName}
            onChange={(e) => setData({ fullName: e.target.value })}
            placeholder="Jane Doe"
            required
            className={valid.fullName ? "is-valid" : data.fullName ? "is-invalid" : ""}
          />
        </ValidWrap>
        <FieldNote>We’ll reference this on your quote and policy docs.</FieldNote>
      </Form.Group>

      <Row className="g-3">
        <Col md={6}>
          <Form.Group>
            <Form.Label>Email *</Form.Label>
            <ValidWrap valid={valid.email}>
              <Form.Control
                type="email"
                value={data.email}
                onChange={(e) => setData({ email: e.target.value })}
                placeholder="name@example.com"
                required
                className={valid.email ? "is-valid" : data.email ? "is-invalid" : ""}
              />
            </ValidWrap>
            <FieldNote>Used for sending quotes and policy updates.</FieldNote>
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group>
            <Form.Label>Phone *</Form.Label>
            <ValidWrap valid={valid.phone}>
              <Form.Control
                value={data.phone}
                onChange={(e) => setData({ phone: e.target.value })}
                placeholder="(555) 555-5555"
                required
                className={valid.phone ? "is-valid" : data.phone ? "is-invalid" : ""}
              />
            </ValidWrap>
            <FieldNote>We’ll call/text for time-sensitive items.</FieldNote>
          </Form.Group>
        </Col>
      </Row>

      <Row className="g-3 mt-1">
        <Col md={12}>
          <Form.Group>
            <Form.Label>Street Address *</Form.Label>
            <ValidWrap valid={valid.street}>
              <Form.Control
                value={data.street}
                onChange={(e) => setData({ street: e.target.value })}
                placeholder="123 Main St"
                required
                className={valid.street ? "is-valid" : data.street ? "is-invalid" : ""}
              />
            </ValidWrap>
          </Form.Group>
        </Col>
      </Row>

      <Row className="g-3 mt-1">
        <Col md={6}>
          <Form.Group>
            <Form.Label>City *</Form.Label>
            <ValidWrap valid={valid.city}>
              <Form.Control
                value={data.city}
                onChange={(e) => setData({ city: e.target.value })}
                placeholder="Denver"
                required
                className={valid.city ? "is-valid" : data.city ? "is-invalid" : ""}
              />
            </ValidWrap>
          </Form.Group>
        </Col>
        <Col md={3}>
          <Form.Group>
            <Form.Label>State *</Form.Label>
            <ValidWrap valid={valid.state}>
              <Form.Control
                value={data.state}
                onChange={(e) => setData({ state: e.target.value.toUpperCase() })}
                placeholder="CO"
                required
                className={valid.state ? "is-valid" : data.state ? "is-invalid" : ""}
              />
            </ValidWrap>
          </Form.Group>
        </Col>
        <Col md={3}>
          <Form.Group>
            <Form.Label>ZIP *</Form.Label>
            <ValidWrap valid={valid.zip}>
              <Form.Control
                value={data.zip}
                onChange={(e) => setData({ zip: e.target.value })}
                placeholder="80202"
                required
                className={valid.zip ? "is-valid" : data.zip ? "is-invalid" : ""}
              />
            </ValidWrap>
          </Form.Group>
        </Col>
      </Row>
    </Card.Body>
  </Card>
);

const Step2Vehicle: React.FC<StepProps> = ({ data, setData, helpEffect, helpKey, valid }) => (
  <Card className="shadow-sm">
    <Card.Body>
      <HelpPanel
        title={STEP_HELP[1].title}
        text={STEP_HELP[1].text}
        effect={helpEffect}
        triggerKey={helpKey}
      />

      <Row className="g-3">
        <Col md={3}>
          <Form.Group>
            <Form.Label>Year *</Form.Label>
            <ValidWrap valid={valid.vehicleYear}>
              <Form.Control
                value={data.vehicleYear}
                onChange={(e) => setData({ vehicleYear: e.target.value })}
                placeholder="2022"
                required
                className={valid.vehicleYear ? "is-valid" : data.vehicleYear ? "is-invalid" : ""}
              />
            </ValidWrap>
          </Form.Group>
        </Col>
        <Col md={4}>
          <Form.Group>
            <Form.Label>Make *</Form.Label>
            <ValidWrap valid={valid.vehicleMake}>
              <Form.Control
                value={data.vehicleMake}
                onChange={(e) => setData({ vehicleMake: e.target.value })}
                placeholder="Toyota"
                required
                className={valid.vehicleMake ? "is-valid" : data.vehicleMake ? "is-invalid" : ""}
              />
            </ValidWrap>
          </Form.Group>
        </Col>
        <Col md={5}>
          <Form.Group>
            <Form.Label>Model *</Form.Label>
            <ValidWrap valid={valid.vehicleModel}>
              <Form.Control
                value={data.vehicleModel}
                onChange={(e) => setData({ vehicleModel: e.target.value })}
                placeholder="Camry SE"
                required
                className={valid.vehicleModel ? "is-valid" : data.vehicleModel ? "is-invalid" : ""}
              />
            </ValidWrap>
          </Form.Group>
        </Col>
      </Row>

      <Row className="g-3 mt-1">
        <Col md={8}>
          <Form.Group>
            <Form.Label>VIN *</Form.Label>
            <ValidWrap valid={valid.vin}>
              <Form.Control
                value={data.vin}
                onChange={(e) => setData({ vin: e.target.value.toUpperCase() })}
                placeholder="17 characters"
                required
                className={valid.vin ? "is-valid" : data.vin ? "is-invalid" : ""}
              />
            </ValidWrap>
            <FieldNote>We use VIN to decode features and ensure accurate rates.</FieldNote>
          </Form.Group>
        </Col>
        <Col md={4}>
          <Form.Group>
            <Form.Label>Ownership *</Form.Label>
            <ValidWrap valid={valid.ownership}>
              <Form.Select
                value={data.ownership}
                onChange={(e) => setData({ ownership: e.target.value })}
                required
                className={valid.ownership ? "is-valid" : data.ownership ? "is-invalid" : ""}
              >
                <option value="">Select…</option>
                <option>Owned</option>
                <option>Financed</option>
                <option>Leased</option>
              </Form.Select>
            </ValidWrap>
          </Form.Group>
        </Col>
      </Row>

      <Row className="g-3 mt-1">
        <Col md={6}>
          <Form.Group>
            <Form.Label>Primary Use *</Form.Label>
            <ValidWrap valid={valid.vehicleUse}>
              <Form.Select
                value={data.vehicleUse}
                onChange={(e) => setData({ vehicleUse: e.target.value })}
                required
                className={valid.vehicleUse ? "is-valid" : data.vehicleUse ? "is-invalid" : ""}
              >
                <option value="">Select…</option>
                <option>Commute</option>
                <option>Business</option>
                <option>Pleasure</option>
                <option>Rideshare</option>
              </Form.Select>
            </ValidWrap>
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group>
            <Form.Label>Annual Mileage *</Form.Label>
            <ValidWrap valid={valid.annualMileage}>
              <Form.Control
                value={data.annualMileage}
                onChange={(e) => setData({ annualMileage: e.target.value })}
                placeholder="e.g., 12000"
                required
                className={valid.annualMileage ? "is-valid" : data.annualMileage ? "is-invalid" : ""}
              />
              <InputGroup.Text>mi/yr</InputGroup.Text>
            </ValidWrap>
          </Form.Group>
        </Col>
      </Row>

      <Row className="g-3 mt-1">
        <Col md={6}>
          <Form.Group>
            <Form.Label>Garaging ZIP *</Form.Label>
            <ValidWrap valid={valid.garagingZip}>
              <Form.Control
                value={data.garagingZip}
                onChange={(e) => setData({ garagingZip: e.target.value })}
                placeholder="80202"
                required
                className={valid.garagingZip ? "is-valid" : data.garagingZip ? "is-invalid" : ""}
              />
            </ValidWrap>
            <FieldNote>Where the car is kept overnight. Use home ZIP if same.</FieldNote>
          </Form.Group>
        </Col>
      </Row>
    </Card.Body>
  </Card>
);

const Step3Drivers: React.FC<StepProps> = ({ data, setData, helpEffect, helpKey, valid }) => (
  <Card className="shadow-sm">
    <Card.Body>
      <HelpPanel
        title={STEP_HELP[2].title}
        text={STEP_HELP[2].text}
        effect={helpEffect}
        triggerKey={helpKey}
      />

      <Row className="g-3">
        <Col md={4}>
          <Form.Group>
            <Form.Label>Date of Birth *</Form.Label>
            <ValidWrap valid={valid.dob}>
              <Form.Control
                value={data.dob}
                onChange={(e) => setData({ dob: e.target.value })}
                placeholder="MM/DD/YYYY"
                required
                className={valid.dob ? "is-valid" : data.dob ? "is-invalid" : ""}
              />
            </ValidWrap>
          </Form.Group>
        </Col>
        <Col md={4}>
          <Form.Group>
            <Form.Label>DL Number *</Form.Label>
            <ValidWrap valid={valid.dlNumber}>
              <Form.Control
                value={data.dlNumber}
                onChange={(e) => setData({ dlNumber: e.target.value.toUpperCase() })}
                placeholder="License #"
                required
                className={valid.dlNumber ? "is-valid" : data.dlNumber ? "is-invalid" : ""}
              />
            </ValidWrap>
          </Form.Group>
        </Col>
        <Col md={4}>
          <Form.Group>
            <Form.Label>DL State *</Form.Label>
            <ValidWrap valid={valid.dlState}>
              <Form.Control
                value={data.dlState}
                onChange={(e) => setData({ dlState: e.target.value.toUpperCase() })}
                placeholder="CO"
                required
                className={valid.dlState ? "is-valid" : data.dlState ? "is-invalid" : ""}
              />
            </ValidWrap>
          </Form.Group>
        </Col>
      </Row>

      <Row className="g-3 mt-1">
        <Col md={4}>
          <Form.Group>
            <Form.Label>Years Licensed *</Form.Label>
            <ValidWrap valid={valid.yearsLicensed}>
              <Form.Control
                value={data.yearsLicensed}
                onChange={(e) => setData({ yearsLicensed: e.target.value })}
                placeholder="e.g., 10"
                required
                className={valid.yearsLicensed ? "is-valid" : data.yearsLicensed ? "is-invalid" : ""}
              />
            </ValidWrap>
          </Form.Group>
        </Col>
        <Col md={4}>
          <Form.Group>
            <Form.Label>Accidents (last 5 yrs) *</Form.Label>
            <ValidWrap valid={valid.accidents5y}>
              <Form.Control
                value={data.accidents5y}
                onChange={(e) => setData({ accidents5y: e.target.value })}
                placeholder="0"
                required
                className={valid.accidents5y ? "is-valid" : data.accidents5y ? "is-invalid" : ""}
              />
            </ValidWrap>
          </Form.Group>
        </Col>
        <Col md={4}>
          <Form.Group>
            <Form.Label>Tickets (last 5 yrs) *</Form.Label>
            <ValidWrap valid={valid.tickets5y}>
              <Form.Control
                value={data.tickets5y}
                onChange={(e) => setData({ tickets5y: e.target.value })}
                placeholder="0"
                required
                className={valid.tickets5y ? "is-valid" : data.tickets5y ? "is-invalid" : ""}
              />
            </ValidWrap>
          </Form.Group>
        </Col>
      </Row>

      <Row className="g-3 mt-1">
        <Col md={6}>
          <Form.Group>
            <Form.Label>SR-22 Required?</Form.Label>
            <ValidWrap valid={true}>
              <Form.Select
                value={data.sr22Required}
                onChange={(e) => setData({ sr22Required: e.target.value })}
              >
                <option value="">Select (optional)…</option>
                <option>Yes</option>
                <option>No</option>
                <option>Not sure</option>
              </Form.Select>
            </ValidWrap>
            <FieldNote>If required, we’ll file the SR-22 with your state.</FieldNote>
          </Form.Group>
        </Col>
      </Row>
    </Card.Body>
  </Card>
);

const Step4Coverage: React.FC<StepProps> = ({ data, setData, helpEffect, helpKey, valid }) => (
  <Card className="shadow-sm">
    <Card.Body>
      <HelpPanel
        title={STEP_HELP[3].title}
        text={STEP_HELP[3].text}
        effect={helpEffect}
        triggerKey={helpKey}
      />

      <Row className="g-3">
        <Col md={6}>
          <Form.Group>
            <Form.Label>Liability Limit *</Form.Label>
            <ValidWrap valid={valid.liabilityLimit}>
              <Form.Select
                value={data.liabilityLimit}
                onChange={(e) => setData({ liabilityLimit: e.target.value })}
                required
                className={valid.liabilityLimit ? "is-valid" : data.liabilityLimit ? "is-invalid" : ""}
              >
                <option value="">Select a limit…</option>
                <option>25/50/25</option>
                <option>50/100/50</option>
                <option>100/300/100</option>
                <option>250/500/250</option>
                <option>$300k CSL</option>
                <option>$500k CSL</option>
              </Form.Select>
            </ValidWrap>
            <FieldNote>Split limits (per person/per accident/property) or CSL (combined single limit).</FieldNote>
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group>
            <Form.Label>Uninsured/Underinsured Motorist</Form.Label>
            <ValidWrap valid={true}>
              <Form.Select
                value={data.uninsuredMotorist}
                onChange={(e) => setData({ uninsuredMotorist: e.target.value })}
              >
                <option value="">Select (optional)…</option>
                <option>25/50</option>
                <option>50/100</option>
                <option>100/300</option>
                <option>250/500</option>
              </Form.Select>
            </ValidWrap>
            <FieldNote>Protects you from at-fault drivers with little/no insurance.</FieldNote>
          </Form.Group>
        </Col>
      </Row>

      <Row className="g-3 mt-1">
        <Col md={6}>
          <Form.Group>
            <Form.Label>Collision Deductible *</Form.Label>
            <ValidWrap valid={valid.collisionDeductible}>
              <Form.Select
                value={data.collisionDeductible}
                onChange={(e) => setData({ collisionDeductible: e.target.value })}
                required
                className={valid.collisionDeductible ? "is-valid" : data.collisionDeductible ? "is-invalid" : ""}
              >
                <option value="">Select…</option>
                <option>$250</option>
                <option>$500</option>
                <option>$1,000</option>
                <option>$2,500</option>
              </Form.Select>
            </ValidWrap>
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group>
            <Form.Label>Comprehensive Deductible *</Form.Label>
            <ValidWrap valid={valid.comprehensiveDeductible}>
              <Form.Select
                value={data.comprehensiveDeductible}
                onChange={(e) => setData({ comprehensiveDeductible: e.target.value })}
                required
                className={valid.comprehensiveDeductible ? "is-valid" : data.comprehensiveDeductible ? "is-invalid" : ""}
              >
                <option value="">Select…</option>
                <option>$250</option>
                <option>$500</option>
                <option>$1,000</option>
                <option>$2,500</option>
              </Form.Select>
            </ValidWrap>
          </Form.Group>
        </Col>
      </Row>

      <Row className="g-3 mt-1">
        <Col md={6}>
          <Form.Group>
            <Form.Label>Medical Payments</Form.Label>
            <ValidWrap valid={true}>
              <Form.Select
                value={data.medicalPayments}
                onChange={(e) => setData({ medicalPayments: e.target.value })}
              >
                <option value="">Select (optional)…</option>
                <option>$2,000</option>
                <option>$5,000</option>
                <option>$10,000</option>
              </Form.Select>
            </ValidWrap>
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group>
            <Form.Label>Roadside Assistance</Form.Label>
            <ValidWrap valid={true}>
              <Form.Select
                value={data.roadsideAssist}
                onChange={(e) => setData({ roadsideAssist: e.target.value })}
              >
                <option value="">Select (optional)…</option>
                <option>Yes</option>
                <option>No</option>
              </Form.Select>
            </ValidWrap>
          </Form.Group>
        </Col>
      </Row>

      <Row className="g-3 mt-1">
        <Col md={6}>
          <Form.Group>
            <Form.Label>Rental Reimbursement</Form.Label>
            <ValidWrap valid={true}>
              <Form.Select
                value={data.rentalReimbursement}
                onChange={(e) => setData({ rentalReimbursement: e.target.value })}
              >
                <option value="">Select (optional)…</option>
                <option>$30/day</option>
                <option>$40/day</option>
                <option>$50/day</option>
              </Form.Select>
            </ValidWrap>
            <FieldNote>Helps cover a rental car while yours is in the shop after a covered claim.</FieldNote>
          </Form.Group>
        </Col>
      </Row>
    </Card.Body>
  </Card>
);

/* ============================================================
   Summary (post-submit)
   ============================================================ */
const SummaryView: React.FC<{ data: WizardData }> = ({ data }) => (
  <Card className="shadow-sm">
    <Card.Body>
      <h5 className="mb-3">Summary</h5>

      <Row className="g-3">
        <Col md={6}>
          <h6 className="text-muted">Contact</h6>
          <div><strong>Name:</strong> {data.fullName || "-"}</div>
          <div><strong>Email:</strong> {data.email || "-"}</div>
          <div><strong>Phone:</strong> {data.phone || "-"}</div>
          <div><strong>Address:</strong> {data.street || "-"}, {data.city || "-"}, {data.state || "-"} {data.zip || "-"}</div>
        </Col>
        <Col md={6}>
          <h6 className="text-muted">Vehicle</h6>
          <div><strong>Year/Make/Model:</strong> {[data.vehicleYear, data.vehicleMake, data.vehicleModel].filter(Boolean).join(" ") || "-"}</div>
          <div><strong>VIN:</strong> {data.vin || "-"}</div>
          <div><strong>Use:</strong> {data.vehicleUse || "-"}</div>
          <div><strong>Mileage:</strong> {data.annualMileage || "-"} mi/yr</div>
          <div><strong>Garaging ZIP:</strong> {data.garagingZip || data.zip || "-"}</div>
        </Col>
      </Row>

      <hr />

      <Row className="g-3">
        <Col md={6}>
          <h6 className="text-muted">Driver</h6>
          <div><strong>DOB:</strong> {data.dob || "-"}</div>
          <div><strong>DL #:</strong> {data.dlNumber || "-"}</div>
          <div><strong>DL State:</strong> {data.dlState || "-"}</div>
          <div><strong>Years Licensed:</strong> {data.yearsLicensed || "-"}</div>
          <div><strong>Accidents (5y):</strong> {data.accidents5y || "-"}</div>
          <div><strong>Tickets (5y):</strong> {data.tickets5y || "-"}</div>
          <div><strong>SR-22 Required:</strong> {data.sr22Required || "-"}</div>
        </Col>
        <Col md={6}>
          <h6 className="text-muted">Coverage</h6>
          <div><strong>Liability:</strong> {data.liabilityLimit || "-"}</div>
          <div><strong>Collision Deductible:</strong> {data.collisionDeductible || "-"}</div>
          <div><strong>Comprehensive Deductible:</strong> {data.comprehensiveDeductible || "-"}</div>
          <div><strong>UM/UIM:</strong> {data.uninsuredMotorist || "-"}</div>
          <div><strong>MedPay:</strong> {data.medicalPayments || "-"}</div>
          <div><strong>Roadside:</strong> {data.roadsideAssist || "-"}</div>
          <div><strong>Rental Reimb.:</strong> {data.rentalReimbursement || "-"}</div>
        </Col>
      </Row>

      <Alert variant="secondary" className="mt-3 mb-0">
        Thanks! We’ll review and follow up if anything else is needed.
      </Alert>
    </Card.Body>
  </Card>
);

/* ============================================================
   Main Component
   ============================================================ */
export const AUTO_Insurance_Wizard_v06: React.FC = () => {
  // activeIndex: 0..3 are data-entry slides, 4 is Summary
  const [activeIndex, setActiveIndex] = useState<number>(0);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [data, setData] = useState<WizardData>(defaultData);

  // Track which steps already animated their help text
  const prevIndexRef = useRef<number | null>(null);
  const helpPlayedRef = useRef<[boolean, boolean, boolean, boolean]>([
    false,
    false,
    false,
    false,
  ]);
  useEffect(() => {
    const prev = prevIndexRef.current;
    if (prev !== null && prev >= 0 && prev <= 3) {
      helpPlayedRef.current[prev] = true;
    }
    prevIndexRef.current = activeIndex;
  }, [activeIndex]);

  // Shallow merge setter
  const patch = useCallback(
    (partial: Partial<WizardData>) => setData((prev) => ({ ...prev, ...partial })),
    []
  );

  // validity & errors
  const { valid, errors } = useMemo(() => buildValidity(data), [data]);

  // Required completion gate
  const requiredOk =
    valid.fullName &&
    valid.email &&
    valid.phone &&
    valid.street &&
    valid.city &&
    valid.state &&
    valid.zip &&

    valid.vehicleYear &&
    valid.vehicleMake &&
    valid.vehicleModel &&
    valid.vin &&
    valid.ownership &&
    valid.vehicleUse &&
    valid.annualMileage &&
    valid.garagingZip &&

    valid.dob &&
    valid.dlNumber &&
    valid.dlState &&
    valid.yearsLicensed &&
    valid.accidents5y &&
    valid.tickets5y &&

    valid.liabilityLimit &&
    valid.collisionDeductible &&
    valid.comprehensiveDeductible;

  const atFirst = activeIndex === 0;
  const atLastDataStep = activeIndex === 3;
  const atSummary = activeIndex === 4;

  const progress = useMemo(() => Math.round(((activeIndex + 1) / 5) * 100), [activeIndex]);

  const handleFinish = async () => {
    setSubmitError(null);
    if (!requiredOk) return;

    setSubmitting(true);
    try {
      const wire = {
        ...data,
        meta: {
          testMode: TEST_MODE,
          version: VERSION,
          submittedAtISO: new Date().toISOString(),
        },
      };

      if (TEST_MODE) {
        await new Promise((r) => setTimeout(r, 600));
        // eslint-disable-next-line no-console
        console.log("[TEST_MODE] Submitting payload:", wire);
      } else {
        const res = await fetch(API_GATEWAY_URL, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(wire),
        });
        if (!res.ok) {
          const txt = await res.text().catch(() => "");
          throw new Error(`API error ${res.status}${txt ? `: ${txt.slice(0, 200)}` : ""}`);
        }
      }

      setActiveIndex(4); // Summary on success
    } catch (err: any) {
      setSubmitError(err?.message ?? "Unknown error submitting form.");
    } finally {
      setSubmitting(false);
    }
  };

  // Determine help effect for current slide (only first appearance)
  const effectiveHelp: HelpEffect =
    helpPlayedRef.current[activeIndex as 0 | 1 | 2 | 3] ? "none" : HELP_EFFECT;

  const stepCards = [
    <Step1Contact
      key="s1"
      data={data}
      setData={patch}
      helpEffect={effectiveHelp}
      helpKey={`help-${activeIndex}`}
      valid={valid}
    />,
    <Step2Vehicle
      key="s2"
      data={data}
      setData={patch}
      helpEffect={effectiveHelp}
      helpKey={`help-${activeIndex}`}
      valid={valid}
    />,
    <Step3Drivers
      key="s3"
      data={data}
      setData={patch}
      helpEffect={effectiveHelp}
      helpKey={`help-${activeIndex}`}
      valid={valid}
    />,
    <Step4Coverage
      key="s4"
      data={data}
      setData={patch}
      helpEffect={effectiveHelp}
      helpKey={`help-${activeIndex}`}
      valid={valid}
    />,
    <SummaryView key="s5" data={data} />,
  ];

  // Right-side info panel content: only show fields that are not valid
  const showRightPanel = !requiredOk && !atSummary && !submitting;
  const rightPanel = (
    <Alert variant="info" className="mb-3">
      <div className="fw-semibold mb-1">What to fix</div>
      <div className="small text-muted">Please complete or correct the following:</div>
      <ul className="mt-2 mb-0 ps-3 small">
        {errors.map((e, i) => (
          <li key={i}>
            <strong>{e.label}</strong> — {e.reason}
          </li>
        ))}
      </ul>
    </Alert>
  );

  return (
    <Container className="py-4">
      {/* Header */}
      <Row className="mb-3 align-items-center">
        <Col lg={9}>
          <h3 className="mb-0">{LABELS.title}</h3>
          <div className="text-muted">{LABELS.subtitle}</div>
        </Col>
        <Col lg={3} className="text-lg-end mt-2 mt-lg-0">
          {SHOW_VERSION && (
            <Badge bg="secondary" pill>
              {VERSION}
            </Badge>
          )}
        </Col>
      </Row>

      <Row>
        {/* Main form column */}
        <Col lg={9}>
          <Row className="mb-2">
            <Col>
              <div className="small text-muted">{LABELS.notices.required}</div>
            </Col>
          </Row>

          {SHOW_PROGRESS_BAR && (
            <Row className="mb-3">
              <Col>
                <ProgressBar now={progress} label={`${progress}%`} />
              </Col>
            </Row>
          )}

          {/* Step headline */}
          <Row className="mb-2">
            <Col>
              <h5 className="mb-0">{LABELS.stepTitles[activeIndex] ?? "Step"}</h5>
            </Col>
          </Row>

          {/* Submission grey-out wrapper */}
          <div className={submitting ? "wizard-submitting" : ""} aria-busy={submitting}>
            {/* Slides */}
            <Carousel
              activeIndex={activeIndex}
              onSelect={() => {}}
              interval={undefined}
              controls={false}
              indicators={false}
              slide
              fade={false}
              className="mb-3"
            >
              {stepCards.map((card, i) => (
                <Carousel.Item key={`slide-${i}`} style={{ transitionDuration: `${SLIDE_ANIMATION_MS}ms` }}>
                  {card}
                </Carousel.Item>
              ))}
            </Carousel>

            {/* Errors */}
            {submitError && (
              <Alert variant="danger" className="mb-3">
                {submitError}
              </Alert>
            )}

            {/* Footer actions */}
            <Row className="g-2">
              <Col xs="auto">
                <Button
                  variant="outline-secondary"
                  onClick={() => activeIndex > 0 && setActiveIndex((i) => i - 1)}
                  disabled={atFirst || submitting}
                >
                  {LABELS.buttons.back}
                </Button>
              </Col>

              {!atSummary && !atLastDataStep && (
                <Col xs="auto">
                  <Button variant="primary" onClick={() => setActiveIndex((i) => i + 1)} disabled={submitting}>
                    {LABELS.buttons.next}
                  </Button>
                </Col>
              )}

              {!atSummary && atLastDataStep && (
                <Col xs="auto">
                  <Button
                    variant={requiredOk ? "success" : "secondary"}
                    onClick={handleFinish}
                    disabled={submitting || !requiredOk}
                  >
                    {submitting ? (
                      <>
                        <Spinner size="sm" animation="border" className="me-2" />
                        Finishing…
                      </>
                    ) : (
                      LABELS.buttons.finish
                    )}
                  </Button>
                </Col>
              )}

              {atSummary && (
                <>
                  <Col xs="auto">
                    <Button variant="outline-primary" onClick={() => setActiveIndex(0)} disabled={submitting}>
                      {LABELS.buttons.edit}
                    </Button>
                  </Col>
                  <Col xs="auto">
                    <Button variant="primary" disabled>
                      {LABELS.buttons.submit}
                    </Button>
                  </Col>
                </>
              )}
            </Row>
          </div>
        </Col>

        {/* Right rail: persistent info panel (no popup) */}
        <Col lg={3} className="mt-3 mt-lg-0">
          {/* Show only fields not completed properly */}
          {!atSummary && (
            <div>
              {!requiredOk && !submitting && (
                <>
                  <HelpPanel title="Heads up" text="A few items still need attention before we can finish." effect="none" />
                  {rightPanel}
                </>
              )}
            </div>
          )}
        </Col>
      </Row>

      {/* Styles */}
      <style>{`
        .carousel-item > .card { width: 100%; }

        /* Typewriter cursor */
        .tw-cursor {
          display: inline-block;
          margin-left: 1px;
          animation: tw-blink 1s step-end infinite;
        }
        @keyframes tw-blink {
          0%, 50% { opacity: 1; }
          50.01%, 100% { opacity: 0; }
        }

        /* Submission overlay feel (light grey + block interactions) */
        .wizard-submitting {
          position: relative;
          pointer-events: none;
          opacity: 0.6;
          filter: grayscale(20%);
        }

        /* Bootstrap valid/invalid outlines appear when classes applied */
        .is-valid { border-color: #198754 !important; }
        .is-invalid { border-color: #dc3545 !important; }
      `}</style>
    </Container>
  );
};
